import React from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Grid,
} from '@material-ui/core';
import {
  formattedCurrency,
  listReferenceTable,
} from '../../../utils/utils';

export const formatClosureDateTime = (dt) => {
  if (!dt) return '---';
  if (dt instanceof Date && !isNaN(dt.getTime())) {
    const day = String(dt.getDate()).padStart(2, '0');
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const year = dt.getFullYear();
    const hour = String(dt.getHours()).padStart(2, '0');
    const min = String(dt.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} às ${hour}:${min}`;
  }
  if (typeof dt === 'string') {
    const trimmed = dt.trim();
    if (/^\d{2}\/\d{2}\/\d{4}/.test(trimmed)) {
      const [dPart, tPart] = trimmed.split(' ');
      if (tPart) {
        const [h, m] = tPart.split(':');
        return `${dPart} às ${h || '00'}:${m || '00'}`;
      }
      return dPart;
    }
    try {
      const parseable = trimmed.replace(' ', 'T');
      const d = new Date(parseable);
      if (!isNaN(d.getTime())) {
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const hour = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} às ${hour}:${min}`;
      }
    } catch (e) {
      // fallback
    }

    const clean = trimmed.replace('T', ' ').replace('Z', '').split('.')[0].trim();
    const [datePart, timePart] = clean.split(' ');
    if (datePart && timePart) {
      const dateParts = datePart.split('-');
      const timeParts = timePart.split(':');
      if (dateParts.length === 3 && timeParts.length >= 2) {
        const [year, month, day] = dateParts;
        const [hour, min] = timeParts;
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year} às ${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
      }
    }
  }
  return String(dt);
};

export const formatClosureTime = (dt) => {
  if (!dt) return '';
  if (dt instanceof Date && !isNaN(dt.getTime())) {
    const hour = String(dt.getHours()).padStart(2, '0');
    const min = String(dt.getMinutes()).padStart(2, '0');
    return `${hour}:${min}`;
  }
  if (typeof dt === 'string') {
    const trimmed = dt.trim();
    if (/^\d{2}\/\d{2}\/\d{4}/.test(trimmed)) {
      const parts = trimmed.split(' ');
      if (parts[1]) {
        const [h, m] = parts[1].split(':');
        return `${(h || '00').padStart(2, '0')}:${(m || '00').padStart(2, '0')}`;
      }
    }
    try {
      const parseable = trimmed.replace(' ', 'T');
      const d = new Date(parseable);
      if (!isNaN(d.getTime())) {
        const hour = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${hour}:${min}`;
      }
    } catch (e) {
      // fallback
    }

    const clean = trimmed.replace('T', ' ').replace('Z', '').split('.')[0].trim();
    const timePart = clean.split(' ')[1] || clean;
    const [hour, min] = timePart.split(':');
    if (hour && min) return `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
  }
  return '';
};

/**
 * Resolve payment method readable name
 */
export const getPaymentMethodName = (move, paymentMethods = []) => {
  if (!move) return 'Outros';

  const pmId = move.reference?.paymentMethod || move.paymentMethod;
  if (pmId) {
    const found = paymentMethods.find((m) => String(m.id) === String(pmId));
    if (found && found.name) return found.name;
  }

  if (typeof move.reference === 'string' && move.reference) {
    return move.reference;
  }
  if (move.reference?.justification) {
    return move.reference.justification;
  }
  if (move.reference?.referenceTable || move.referenceTable) {
    try {
      const refName = listReferenceTable(move.reference?.referenceTable || move.referenceTable);
      if (refName) return refName;
    } catch (e) {
      // fallback
    }
  }
  return 'Administrativo / Outros';
};

/**
 * Resolve transaction type / reference description
 */
export const getMoveReferenceDescription = (move) => {
  if (!move) return '---';

  let refTableName = '';
  try {
    refTableName = listReferenceTable(move.reference?.referenceTable || move.referenceTable) || '';
  } catch (e) {
    refTableName = '';
  }

  const refId = move.reference?.referenceId || move.move;
  const just = move.reference?.justification;

  if (just && refTableName) {
    return `${refTableName}: ${just}`;
  }
  if (just) return just;
  if (refTableName && refId) {
    return `${refTableName} #${refId}`;
  }
  if (refTableName) return refTableName;
  return `Movimento #${move.move || ''}`;
};

/**
 * Group entries by payment method
 */
export const groupMovesByPaymentMethod = (moves = [], paymentMethods = []) => {
  const groups = {};
  let totalSum = 0;

  moves.forEach((move) => {
    const methodName = getPaymentMethodName(move, paymentMethods);
    const val = Math.abs(Number(move.value) || 0);
    totalSum += val;

    if (!groups[methodName]) {
      groups[methodName] = {
        name: methodName,
        count: 0,
        total: 0,
      };
    }
    groups[methodName].count += 1;
    groups[methodName].total += val;
  });

  return Object.values(groups)
    .map((g) => ({
      ...g,
      percentage: totalSum > 0 ? ((g.total / totalSum) * 100).toFixed(1) : '0.0',
    }))
    .sort((a, b) => b.total - a.total);
};

/**
 * Group outs by category/payment method
 */
export const groupOutsByCategory = (moves = [], paymentMethods = []) => {
  const groups = {};
  let totalSum = 0;

  moves.forEach((move) => {
    let category = '';
    const pmId = move.reference?.paymentMethod || move.paymentMethod;
    const methodObj = paymentMethods.find((m) => String(m.id) === String(pmId));
    let refTable = '';
    try {
      refTable = listReferenceTable(move.reference?.referenceTable || move.referenceTable) || '';
    } catch (e) {
      refTable = '';
    }
    const justification = move.reference?.justification;

    if (justification) {
      category = justification;
      if (methodObj) category += ` (${methodObj.name})`;
    } else if (methodObj) {
      category = methodObj.name;
      if (refTable) category += ` - ${refTable}`;
    } else if (refTable) {
      category = refTable;
    } else {
      category = 'Saída Administrativa';
    }

    const val = Math.abs(Number(move.value) || 0);
    totalSum += val;

    if (!groups[category]) {
      groups[category] = {
        name: category,
        count: 0,
        total: 0,
      };
    }
    groups[category].count += 1;
    groups[category].total += val;
  });

  return Object.values(groups)
    .map((g) => ({
      ...g,
      percentage: totalSum > 0 ? ((g.total / totalSum) * 100).toFixed(1) : '0.0',
    }))
    .sort((a, b) => b.total - a.total);
};

/**
 * Consolidated payment method summary (Entradas, Saídas, Saldo Líquido)
 */
export const consolidatedPaymentMethodsSummary = (entriesMoves = [], outsMoves = [], paymentMethods = []) => {
  const summary = {};

  entriesMoves.forEach((move) => {
    const name = getPaymentMethodName(move, paymentMethods);
    const val = Math.abs(Number(move.value) || 0);
    if (!summary[name]) {
      summary[name] = { name, entries: 0, outs: 0, balance: 0, count: 0 };
    }
    summary[name].entries += val;
    summary[name].balance += val;
    summary[name].count += 1;
  });

  outsMoves.forEach((move) => {
    const name = getPaymentMethodName(move, paymentMethods);
    const val = Math.abs(Number(move.value) || 0);
    if (!summary[name]) {
      summary[name] = { name, entries: 0, outs: 0, balance: 0, count: 0 };
    }
    summary[name].outs += val;
    summary[name].balance -= val;
    summary[name].count += 1;
  });

  return Object.values(summary).sort((a, b) => b.entries - a.entries);
};

/* =========================================================================
 * THERMAL TEMPLATE (80mm)
 * ========================================================================= */
export const CashierThermalTemplate = ({
  cashier,
  closure,
  movesData,
  paymentMethods = [],
  credencials = {},
  options = {},
}) => {
  const printMode = options.printMode || 'agrupado'; // 'agrupado' | 'unitario' | 'completo'

  const entriesMoves = movesData?.entries?.moves || [];
  const outsMoves = movesData?.outs?.moves || [];
  const discountsMoves = movesData?.discounts?.moves || [];

  const totalEntries = Number(movesData?.entries?.total ?? entriesMoves.reduce((acc, m) => acc + Math.abs(Number(m.value) || 0), 0));
  const totalOuts = Math.abs(Number(movesData?.outs?.total ?? outsMoves.reduce((acc, m) => acc + Math.abs(Number(m.value) || 0), 0)));
  const totalDiscounts = Math.abs(Number(movesData?.discounts?.total ?? discountsMoves.reduce((acc, m) => acc + Math.abs(Number(m.value) || 0), 0)));
  const balance = totalEntries - totalOuts;
  const totalBustling = Number(movesData?.totalBustling) || (totalEntries + totalOuts);

  const rawInitial = closure?.initalValue ?? closure?.initialValue;
  const initialCash =
    rawInitial !== undefined && rawInitial !== null && Number(rawInitial) > 0
      ? Number(rawInitial)
      : movesData?.initialValue !== undefined && movesData?.initialValue !== null
      ? Number(movesData.initialValue)
      : cashier?.currentValue !== undefined && movesData?.totalBustling !== undefined
      ? Math.max(0, Number((Number(cashier.currentValue) - Number(movesData.totalBustling)).toFixed(2)))
      : Number(rawInitial || 0);
  const finalCash = closure?.finalValue !== undefined && closure?.finalValue !== null ? Number(closure.finalValue) : null;
  const saldoEmCaixa = (closure?.closed && finalCash !== null) ? finalCash : (initialCash + balance);

  const groupedEntries = groupMovesByPaymentMethod(entriesMoves, paymentMethods);
  const groupedOuts = groupOutsByCategory(outsMoves, paymentMethods);
  const consolidated = consolidatedPaymentMethodsSummary(entriesMoves, outsMoves, paymentMethods);

  const showGrouped = printMode === 'agrupado' || printMode === 'completo';
  const showUnitary = printMode === 'unitario' || printMode === 'completo';

  const companyName = credencials?.companyData?.pseudonimo || credencials?.companyData?.nome || 'Duzz Commercial';
  const cnpj = credencials?.companyData?.showCNPJ ? credencials?.companyData?.cnpj : null;

  return (
    <Box
      className="cashier-thermal-print"
      style={{
        backgroundColor: '#fff',
        color: '#000',
        width: '80mm',
        margin: '0 auto',
        fontFamily: 'Roboto, monospace, sans-serif',
        padding: '5mm 3mm',
        boxSizing: 'border-box',
        fontSize: '9pt',
        lineHeight: 1.3,
      }}
    >
      {/* Cabeçalho da Empresa */}
      <Box textAlign="center" mb={1}>
        <Typography variant="subtitle1" style={{ fontWeight: 'bold', fontSize: '13pt', lineHeight: 1.2 }}>
          {companyName}
        </Typography>
        {cnpj && (
          <Typography variant="caption" display="block">
            CNPJ: {cnpj}
          </Typography>
        )}
      </Box>

      {/* Linha divisória */}
      <Box py={0.5} my={0.5} borderTop="1px dashed #000" borderBottom="1px dashed #000" textAlign="center">
        <Typography variant="body2" style={{ fontWeight: 'bold', letterSpacing: 1 }}>
          *** FECHAMENTO DE CAIXA ***
        </Typography>
        <Typography variant="caption" display="block">
          MODO: {printMode.toUpperCase()}
        </Typography>
      </Box>

      {/* Informações do Caixa */}
      <Table size="small" style={{ width: '100%', marginBottom: 6 }}>
        <TableBody>
          <TableRow>
            <TableCell style={{ border: 'none', padding: '2px 0', fontWeight: 'bold' }}>Caixa:</TableCell>
            <TableCell align="right" style={{ border: 'none', padding: '2px 0' }}>
              {cashier?.name || 'Caixa'}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={{ border: 'none', padding: '2px 0' }}>Abertura:</TableCell>
            <TableCell align="right" style={{ border: 'none', padding: '2px 0' }}>
              {formatClosureDateTime(closure?.opened)}
            </TableCell>
          </TableRow>
          {closure?.openedBy && (
            <TableRow>
              <TableCell style={{ border: 'none', padding: '2px 0' }}>Aberto por:</TableCell>
              <TableCell align="right" style={{ border: 'none', padding: '2px 0' }}>
                {closure.openedBy}
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell style={{ border: 'none', padding: '2px 0' }}>Fechamento:</TableCell>
            <TableCell align="right" style={{ border: 'none', padding: '2px 0' }}>
              {closure?.closed ? formatClosureDateTime(closure.closed) : 'Em Aberto'}
            </TableCell>
          </TableRow>
          {closure?.closedBy && (
            <TableRow>
              <TableCell style={{ border: 'none', padding: '2px 0' }}>Fechado por:</TableCell>
              <TableCell align="right" style={{ border: 'none', padding: '2px 0' }}>
                {closure.closedBy}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Resumo Financeiro Geral */}
      <Box pt={0.5} borderTop="1px dashed #000">
        <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
          RESUMO GERAL
        </Typography>
        <Table size="small" style={{ width: '100%' }}>
          <TableBody>
            <TableRow>
              <TableCell style={{ border: 'none', padding: '2px 0' }}>Fundo Inicial:</TableCell>
              <TableCell align="right" style={{ border: 'none', padding: '2px 0' }}>
                {formattedCurrency(initialCash)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ border: 'none', padding: '2px 0' }}>(+) Total Entradas:</TableCell>
              <TableCell align="right" style={{ border: 'none', padding: '2px 0', fontWeight: 'bold' }}>
                {formattedCurrency(totalEntries)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ border: 'none', padding: '2px 0' }}>(-) Total Saídas:</TableCell>
              <TableCell align="right" style={{ border: 'none', padding: '2px 0', color: '#c00' }}>
                -{formattedCurrency(totalOuts)}
              </TableCell>
            </TableRow>
            {totalDiscounts > 0 && (
              <TableRow>
                <TableCell style={{ border: 'none', padding: '2px 0' }}>(-) Descontos:</TableCell>
                <TableCell align="right" style={{ border: 'none', padding: '2px 0' }}>
                  -{formattedCurrency(totalDiscounts)}
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell style={{ borderTop: '1px solid #000', padding: '4px 0', fontWeight: 'bold' }}>
                (=) Balanço Período:
              </TableCell>
              <TableCell align="right" style={{ borderTop: '1px solid #000', padding: '4px 0', fontWeight: 'bold' }}>
                {formattedCurrency(balance)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ borderTop: '1px dashed #000', padding: '4px 0', fontWeight: 'bold' }}>
                {closure?.closed ? '(=) Saldo Final Caixa:' : '(=) Saldo Atual Caixa:'}
              </TableCell>
              <TableCell align="right" style={{ borderTop: '1px dashed #000', padding: '4px 0', fontWeight: 'bold' }}>
                {formattedCurrency(saldoEmCaixa)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ border: 'none', padding: '2px 0', fontSize: '8pt', color: '#555' }}>
                Total Movimentado:
              </TableCell>
              <TableCell align="right" style={{ border: 'none', padding: '2px 0', fontSize: '8pt', color: '#555' }}>
                {formattedCurrency(totalBustling)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {/* SEÇÃO AGRUPADA */}
      {showGrouped && (
        <>
          {/* Entradas Agrupadas por Método */}
          <Box pt={1} mt={1} borderTop="1px dashed #000">
            <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
              ENTRADAS POR MÉTODO ({entriesMoves.length})
            </Typography>
            {groupedEntries.length === 0 ? (
              <Typography variant="caption" color="textSecondary">Sem entradas no período.</Typography>
            ) : (
              <Table size="small" style={{ width: '100%' }}>
                <TableBody>
                  {groupedEntries.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell style={{ border: 'none', padding: '2px 0' }}>
                        {item.name} <span style={{ fontSize: '7.5pt', color: '#555' }}>({item.count}x • {item.percentage}%)</span>
                      </TableCell>
                      <TableCell align="right" style={{ border: 'none', padding: '2px 0', fontWeight: 'bold' }}>
                        {formattedCurrency(item.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell style={{ borderTop: '1px solid #000', padding: '3px 0', fontWeight: 'bold' }}>
                      Subtotal Entradas:
                    </TableCell>
                    <TableCell align="right" style={{ borderTop: '1px solid #000', padding: '3px 0', fontWeight: 'bold' }}>
                      {formattedCurrency(totalEntries)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </Box>

          {/* Saídas Agrupadas por Categoria */}
          <Box pt={1} mt={1} borderTop="1px dashed #000">
            <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
              SAÍDAS POR MOTIVO / MÉTODO ({outsMoves.length})
            </Typography>
            {groupedOuts.length === 0 ? (
              <Typography variant="caption" color="textSecondary">Sem saídas no período.</Typography>
            ) : (
              <Table size="small" style={{ width: '100%' }}>
                <TableBody>
                  {groupedOuts.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell style={{ border: 'none', padding: '2px 0' }}>
                        {item.name} <span style={{ fontSize: '7.5pt', color: '#555' }}>({item.count}x • {item.percentage}%)</span>
                      </TableCell>
                      <TableCell align="right" style={{ border: 'none', padding: '2px 0', fontWeight: 'bold', color: '#c00' }}>
                        -{formattedCurrency(item.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell style={{ borderTop: '1px solid #000', padding: '3px 0', fontWeight: 'bold' }}>
                      Subtotal Saídas:
                    </TableCell>
                    <TableCell align="right" style={{ borderTop: '1px solid #000', padding: '3px 0', fontWeight: 'bold', color: '#c00' }}>
                      -{formattedCurrency(totalOuts)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </Box>

          {/* Consolidação por Método */}
          {consolidated.length > 0 && (
            <Box pt={1} mt={1} borderTop="1px dashed #000">
              <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                CONSOLIDAÇÃO DE PAGAMENTOS
              </Typography>
              <Table size="small" style={{ width: '100%' }}>
                <TableBody>
                  {consolidated.map((c, idx) => (
                    <TableRow key={idx}>
                      <TableCell style={{ border: 'none', padding: '2px 0' }}>
                        <div><strong>{c.name}</strong></div>
                        <div style={{ fontSize: '7.5pt', color: '#666' }}>
                          Ent: {formattedCurrency(c.entries)} | Saí: {formattedCurrency(c.outs)}
                        </div>
                      </TableCell>
                      <TableCell align="right" style={{ border: 'none', padding: '2px 0', verticalAlign: 'top', fontWeight: 'bold' }}>
                        {formattedCurrency(c.balance)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      )}

      {/* SEÇÃO UNITÁRIA (LANÇAMENTO A LANÇAMENTO) */}
      {showUnitary && (
        <>
          {/* Lançamentos de Entrada */}
          <Box pt={1} mt={1} borderTop="1px dashed #000">
            <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
              ENTRADAS DETALHADAS ({entriesMoves.length})
            </Typography>
            {entriesMoves.length === 0 ? (
              <Typography variant="caption" color="textSecondary">Nenhuma movimentação de entrada.</Typography>
            ) : (
              <Table size="small" style={{ width: '100%' }}>
                <TableBody>
                  {entriesMoves.map((m, idx) => (
                    <TableRow key={idx}>
                      <TableCell style={{ borderBottom: '1px dashed #eee', padding: '2px 0' }}>
                        <div>
                          <span style={{ fontSize: '8pt', color: '#555' }}>{formatClosureTime(m.moment)} </span>
                          <strong>#{m.move}</strong> • {getPaymentMethodName(m, paymentMethods)}
                        </div>
                        <div style={{ fontSize: '7.5pt', color: '#666' }}>
                          {getMoveReferenceDescription(m)}
                        </div>
                      </TableCell>
                      <TableCell align="right" style={{ borderBottom: '1px dashed #eee', padding: '2px 0', verticalAlign: 'top', fontWeight: 'bold' }}>
                        {formattedCurrency(Math.abs(Number(m.value) || 0))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>

          {/* Lançamentos de Saída */}
          <Box pt={1} mt={1} borderTop="1px dashed #000">
            <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
              SAÍDAS DETALHADAS ({outsMoves.length})
            </Typography>
            {outsMoves.length === 0 ? (
              <Typography variant="caption" color="textSecondary">Nenhuma movimentação de saída.</Typography>
            ) : (
              <Table size="small" style={{ width: '100%' }}>
                <TableBody>
                  {outsMoves.map((m, idx) => (
                    <TableRow key={idx}>
                      <TableCell style={{ borderBottom: '1px dashed #eee', padding: '2px 0' }}>
                        <div>
                          <span style={{ fontSize: '8pt', color: '#555' }}>{formatClosureTime(m.moment)} </span>
                          <strong>#{m.move}</strong> • {getPaymentMethodName(m, paymentMethods)}
                        </div>
                        <div style={{ fontSize: '7.5pt', color: '#666' }}>
                          {getMoveReferenceDescription(m)}
                        </div>
                      </TableCell>
                      <TableCell align="right" style={{ borderBottom: '1px dashed #eee', padding: '2px 0', verticalAlign: 'top', fontWeight: 'bold', color: '#c00' }}>
                        -{formattedCurrency(Math.abs(Number(m.value) || 0))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </>
      )}

      {/* Rodapé e Assinatura */}
      <Box mt={2} pt={1} borderTop="1px dashed #000" textAlign="center">
        <Typography variant="caption" display="block" style={{ fontStyle: 'italic', fontSize: '7.5pt' }}>
          Documento não fiscal para conferência de caixa.
        </Typography>
        <Typography variant="caption" display="block" style={{ fontSize: '7.5pt', marginTop: 2 }}>
          Emitido em {new Date().toLocaleString('pt-BR')}
        </Typography>
        <Box mt={3} pt={2} borderTop="1px solid #333">
          <Typography variant="caption" display="block" style={{ fontSize: '8pt' }}>
            Assinatura do Operador / Responsável
          </Typography>
        </Box>
        <Typography variant="body2" style={{ marginTop: '8px', fontSize: '8pt' }}>
          ✂ - - - - - - - - - - - - - - - - - - - -
        </Typography>
      </Box>
    </Box>
  );
};

/* =========================================================================
 * A4 TEMPLATE (Relatório Gerencial A4)
 * ========================================================================= */
export const CashierA4Template = ({
  cashier,
  closure,
  movesData,
  paymentMethods = [],
  credencials = {},
  options = {},
}) => {
  const printMode = options.printMode || 'agrupado';

  const entriesMoves = movesData?.entries?.moves || [];
  const outsMoves = movesData?.outs?.moves || [];
  const discountsMoves = movesData?.discounts?.moves || [];

  const totalEntries = Number(movesData?.entries?.total ?? entriesMoves.reduce((acc, m) => acc + Math.abs(Number(m.value) || 0), 0));
  const totalOuts = Math.abs(Number(movesData?.outs?.total ?? outsMoves.reduce((acc, m) => acc + Math.abs(Number(m.value) || 0), 0)));
  const totalDiscounts = Math.abs(Number(movesData?.discounts?.total ?? discountsMoves.reduce((acc, m) => acc + Math.abs(Number(m.value) || 0), 0)));
  const balance = totalEntries - totalOuts;
  const totalBustling = Number(movesData?.totalBustling) || (totalEntries + totalOuts);

  const rawInitial = closure?.initalValue ?? closure?.initialValue;
  const initialCash =
    rawInitial !== undefined && rawInitial !== null && Number(rawInitial) > 0
      ? Number(rawInitial)
      : movesData?.initialValue !== undefined && movesData?.initialValue !== null
      ? Number(movesData.initialValue)
      : cashier?.currentValue !== undefined && movesData?.totalBustling !== undefined
      ? Math.max(0, Number((Number(cashier.currentValue) - Number(movesData.totalBustling)).toFixed(2)))
      : Number(rawInitial || 0);
  const finalCash = closure?.finalValue !== undefined && closure?.finalValue !== null ? Number(closure.finalValue) : null;
  const saldoEmCaixa = (closure?.closed && finalCash !== null) ? finalCash : (initialCash + balance);

  const groupedEntries = groupMovesByPaymentMethod(entriesMoves, paymentMethods);
  const groupedOuts = groupOutsByCategory(outsMoves, paymentMethods);
  const consolidated = consolidatedPaymentMethodsSummary(entriesMoves, outsMoves, paymentMethods);

  const showGrouped = printMode === 'agrupado' || printMode === 'completo';
  const showUnitary = printMode === 'unitario' || printMode === 'completo';

  const companyName = credencials?.companyData?.pseudonimo || credencials?.companyData?.nome || 'Duzz Commercial';
  const cnpj = credencials?.companyData?.showCNPJ ? credencials?.companyData?.cnpj : null;

  const s = {
    page: {
      backgroundColor: '#fff',
      color: '#111',
      width: '794px',
      minWidth: '794px',
      maxWidth: '794px',
      margin: '0 auto',
      padding: '12mm 14mm',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      fontSize: '9.5pt',
      lineHeight: 1.4,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      borderBottom: '2px solid #1a365d',
      paddingBottom: '12px',
      marginBottom: '16px',
    },
    companyTitle: {
      fontSize: '18pt',
      fontWeight: 'bold',
      color: '#1a365d',
      margin: 0,
    },
    reportTitle: {
      fontSize: '14pt',
      fontWeight: 'bold',
      color: '#333',
      textAlign: 'right',
      margin: 0,
    },
    metaBox: {
      backgroundColor: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      padding: '10px 14px',
      marginBottom: '16px',
    },
    sectionTitle: {
      fontSize: '11pt',
      fontWeight: 'bold',
      color: '#1a365d',
      borderBottom: '1.5px solid #cbd5e1',
      paddingBottom: '4px',
      marginTop: '16px',
      marginBottom: '8px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    card: {
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      padding: '8px 10px',
      textAlign: 'center',
      backgroundColor: '#fff',
      boxSizing: 'border-box',
    },
    cardValue: {
      fontSize: '12pt',
      fontWeight: 'bold',
      marginTop: '4px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '12px',
      fontSize: '9pt',
    },
    th: {
      backgroundColor: '#f1f5f9',
      color: '#334155',
      fontWeight: 'bold',
      border: '1px solid #cbd5e1',
      padding: '6px 8px',
      textAlign: 'left',
    },
    td: {
      border: '1px solid #e2e8f0',
      padding: '5px 8px',
    },
    tdRight: {
      border: '1px solid #e2e8f0',
      padding: '5px 8px',
      textAlign: 'right',
    },
  };

  return (
    <div className="cashier-a4-print" style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.companyTitle}>{companyName}</h1>
          {cnpj && <div style={{ fontSize: '9pt', color: '#64748b' }}>CNPJ: {cnpj}</div>}
          <div style={{ fontSize: '8.5pt', color: '#64748b' }}>Sistema de Gestão Comercial</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <h2 style={s.reportTitle}>FECHAMENTO DE CAIXA</h2>
          <div style={{ fontSize: '9pt', color: '#475569', marginTop: 4 }}>
            Formato: <strong>{printMode === 'completo' ? 'Completo (Agrupado + Unitário)' : printMode === 'unitario' ? 'Unitário (Analítico)' : 'Agrupado (Sintético)'}</strong>
          </div>
          <div style={{ fontSize: '8.5pt', color: '#64748b' }}>
            Emissão: {new Date().toLocaleString('pt-BR')}
          </div>
        </div>
      </div>

      {/* Meta Box: Dados do Caixa e Período */}
      <div style={s.metaBox}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <div style={{ fontSize: '8pt', color: '#64748b' }}>CAIXA</div>
            <div style={{ fontWeight: 'bold', fontSize: '10.5pt' }}>{cashier?.name || 'Caixa'}</div>
          </Grid>
          <Grid item xs={3}>
            <div style={{ fontSize: '8pt', color: '#64748b' }}>ABERTURA</div>
            <div style={{ fontWeight: '500' }}>{formatClosureDateTime(closure?.opened)}</div>
            {closure?.openedBy && (
              <div style={{ fontSize: '8pt', color: '#64748b' }}>Por: {closure.openedBy}</div>
            )}
          </Grid>
          <Grid item xs={3}>
            <div style={{ fontSize: '8pt', color: '#64748b' }}>FECHAMENTO</div>
            <div style={{ fontWeight: '500' }}>
              {closure?.closed ? formatClosureDateTime(closure.closed) : 'Caixa em Aberto'}
            </div>
            {closure?.closedBy && (
              <div style={{ fontSize: '8pt', color: '#64748b' }}>Por: {closure.closedBy}</div>
            )}
          </Grid>
          <Grid item xs={3}>
            <div style={{ fontSize: '8pt', color: '#64748b' }}>TOTAL TRANSAÇÕES</div>
            <div style={{ fontWeight: 'bold', fontSize: '10.5pt' }}>
              {entriesMoves.length + outsMoves.length} lançamentos
            </div>
          </Grid>
        </Grid>
      </div>

      {/* Cards de Totais Financeiros: 2 fileiras de 3 cards (xs=4) com amplo espaço */}
      <Grid container spacing={1} style={{ marginBottom: 12 }}>
        <Grid item xs={4}>
          <div style={s.card}>
            <div style={{ fontSize: '7.5pt', color: '#64748b' }}>FUNDO INICIAL</div>
            <div style={{ ...s.cardValue, color: '#334155' }}>{formattedCurrency(initialCash)}</div>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div style={s.card}>
            <div style={{ fontSize: '7.5pt', color: '#16a34a' }}>TOTAL ENTRADAS</div>
            <div style={{ ...s.cardValue, color: '#16a34a' }}>{formattedCurrency(totalEntries)}</div>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div style={s.card}>
            <div style={{ fontSize: '7.5pt', color: '#dc2626' }}>TOTAL SAÍDAS</div>
            <div style={{ ...s.cardValue, color: '#dc2626' }}>-{formattedCurrency(totalOuts)}</div>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div style={s.card}>
            <div style={{ fontSize: '7.5pt', color: '#d97706' }}>DESCONTOS</div>
            <div style={{ ...s.cardValue, color: '#d97706' }}>{formattedCurrency(totalDiscounts)}</div>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div style={{ ...s.card, backgroundColor: balance >= 0 ? '#f0fdf4' : '#fef2f2', borderColor: balance >= 0 ? '#bbf7d0' : '#fecaca' }}>
            <div style={{ fontSize: '7.5pt', color: balance >= 0 ? '#15803d' : '#b91c1c' }}>BALANÇO LÍQUIDO</div>
            <div style={{ ...s.cardValue, color: balance >= 0 ? '#15803d' : '#b91c1c' }}>{formattedCurrency(balance)}</div>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div style={s.card}>
            <div style={{ fontSize: '7.5pt', color: '#0284c7' }}>TOTAL MOVIMENTADO</div>
            <div style={{ ...s.cardValue, color: '#0284c7' }}>{formattedCurrency(totalBustling)}</div>
          </div>
        </Grid>
      </Grid>

      {/* Banner de Saldo em Caixa */}
      <div
        style={{
          ...s.metaBox,
          backgroundColor: '#eff6ff',
          borderColor: '#bfdbfe',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 16px',
          marginBottom: 16,
        }}
      >
        <div>
          <div style={{ fontSize: '8.5pt', fontWeight: 'bold', color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {closure?.closed ? 'Saldo Final do Caixa (Fundo Inicial + Balanço)' : 'Saldo Atual em Caixa (Fundo Inicial + Balanço)'}
          </div>
          <div style={{ fontSize: '8pt', color: '#64748b', marginTop: 2 }}>
            Valor total apurado e conciliado em caixa
          </div>
        </div>
        <div style={{ fontSize: '15pt', fontWeight: 'bold', color: '#1e3a8a' }}>
          {formattedCurrency(saldoEmCaixa)}
        </div>
      </div>

      {/* SEÇÃO AGRUPADA */}
      {showGrouped && (
        <>
          <div style={s.sectionTitle}>Resumo por Método de Pagamento e Categoria</div>
          <Grid container spacing={2}>
            {/* Tabela Entradas Agrupadas */}
            <Grid item xs={6}>
              <div style={{ fontWeight: 'bold', fontSize: '9pt', marginBottom: 4, color: '#16a34a' }}>
                Entradas por Método ({entriesMoves.length} lançamentos)
              </div>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Método de Pagamento</th>
                    <th style={{ ...s.th, textAlign: 'center', width: '50px' }}>Qtd</th>
                    <th style={{ ...s.th, textAlign: 'center', width: '50px' }}>%</th>
                    <th style={{ ...s.th, textAlign: 'right', width: '100px' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedEntries.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ ...s.td, textAlign: 'center', color: '#64748b' }}>
                        Nenhuma entrada registrada
                      </td>
                    </tr>
                  ) : (
                    groupedEntries.map((item, idx) => (
                      <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                        <td style={s.td}><strong>{item.name}</strong></td>
                        <td style={{ ...s.td, textAlign: 'center' }}>{item.count}</td>
                        <td style={{ ...s.td, textAlign: 'center' }}>{item.percentage}%</td>
                        <td style={s.tdRight}><strong>{formattedCurrency(item.total)}</strong></td>
                      </tr>
                    ))
                  )}
                  <tr style={{ backgroundColor: '#f1f5f9' }}>
                    <td colSpan={3} style={{ ...s.td, fontWeight: 'bold' }}>Total de Entradas:</td>
                    <td style={{ ...s.tdRight, fontWeight: 'bold', color: '#16a34a' }}>{formattedCurrency(totalEntries)}</td>
                  </tr>
                </tbody>
              </table>
            </Grid>

            {/* Tabela Saídas Agrupadas */}
            <Grid item xs={6}>
              <div style={{ fontWeight: 'bold', fontSize: '9pt', marginBottom: 4, color: '#dc2626' }}>
                Saídas por Motivo / Método ({outsMoves.length} lançamentos)
              </div>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Motivo / Método</th>
                    <th style={{ ...s.th, textAlign: 'center', width: '50px' }}>Qtd</th>
                    <th style={{ ...s.th, textAlign: 'center', width: '50px' }}>%</th>
                    <th style={{ ...s.th, textAlign: 'right', width: '100px' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedOuts.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ ...s.td, textAlign: 'center', color: '#64748b' }}>
                        Nenhuma saída registrada
                      </td>
                    </tr>
                  ) : (
                    groupedOuts.map((item, idx) => (
                      <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                        <td style={s.td}><strong>{item.name}</strong></td>
                        <td style={{ ...s.td, textAlign: 'center' }}>{item.count}</td>
                        <td style={{ ...s.td, textAlign: 'center' }}>{item.percentage}%</td>
                        <td style={{ ...s.tdRight, color: '#dc2626' }}><strong>-{formattedCurrency(item.total)}</strong></td>
                      </tr>
                    ))
                  )}
                  <tr style={{ backgroundColor: '#f1f5f9' }}>
                    <td colSpan={3} style={{ ...s.td, fontWeight: 'bold' }}>Total de Saídas:</td>
                    <td style={{ ...s.tdRight, fontWeight: 'bold', color: '#dc2626' }}>-{formattedCurrency(totalOuts)}</td>
                  </tr>
                </tbody>
              </table>
            </Grid>
          </Grid>

          {/* Consolidação por Método */}
          {consolidated.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 'bold', fontSize: '9pt', marginBottom: 4, color: '#1a365d' }}>
                Balanço Consolidado por Método de Pagamento
              </div>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Método de Pagamento</th>
                    <th style={{ ...s.th, textAlign: 'center', width: '70px' }}>Transações</th>
                    <th style={{ ...s.th, textAlign: 'right', width: '120px' }}>(+) Entradas</th>
                    <th style={{ ...s.th, textAlign: 'right', width: '120px' }}>(-) Saídas</th>
                    <th style={{ ...s.th, textAlign: 'right', width: '120px' }}>(=) Saldo Líquido</th>
                  </tr>
                </thead>
                <tbody>
                  {consolidated.map((c, idx) => (
                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <td style={s.td}><strong>{c.name}</strong></td>
                      <td style={{ ...s.td, textAlign: 'center' }}>{c.count}</td>
                      <td style={{ ...s.tdRight, color: '#16a34a' }}>{formattedCurrency(c.entries)}</td>
                      <td style={{ ...s.tdRight, color: '#dc2626' }}>{c.outs > 0 ? `-${formattedCurrency(c.outs)}` : 'R$ 0,00'}</td>
                      <td style={{ ...s.tdRight, fontWeight: 'bold', color: c.balance >= 0 ? '#15803d' : '#b91c1c' }}>
                        {formattedCurrency(c.balance)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#e2e8f0', fontWeight: 'bold' }}>
                    <td style={s.td}>TOTAL GERAL CONSOLIDADO</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{entriesMoves.length + outsMoves.length}</td>
                    <td style={{ ...s.tdRight, color: '#16a34a' }}>{formattedCurrency(totalEntries)}</td>
                    <td style={{ ...s.tdRight, color: '#dc2626' }}>-{formattedCurrency(totalOuts)}</td>
                    <td style={{ ...s.tdRight, color: balance >= 0 ? '#15803d' : '#b91c1c' }}>{formattedCurrency(balance)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* SEÇÃO UNITÁRIA */}
      {showUnitary && (
        <>
          <div style={s.sectionTitle}>Relação Analítica de Movimentações</div>

          {/* Entradas Analíticas */}
          <div style={{ fontWeight: 'bold', fontSize: '9pt', marginBottom: 4, color: '#16a34a' }}>
            Entradas Detalhadas ({entriesMoves.length} itens)
          </div>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '50px' }}>ID</th>
                <th style={{ ...s.th, width: '120px' }}>Data / Hora</th>
                <th style={{ ...s.th, width: '130px' }}>Método</th>
                <th style={s.th}>Origem / Referência</th>
                <th style={{ ...s.th, textAlign: 'right', width: '90px' }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {entriesMoves.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ ...s.td, textAlign: 'center', color: '#64748b' }}>
                    Nenhuma entrada registrada
                  </td>
                </tr>
              ) : (
                entriesMoves.map((m, idx) => (
                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={s.td}>#{m.move}</td>
                    <td style={s.td}>{formatClosureDateTime(m.moment)}</td>
                    <td style={s.td}><strong>{getPaymentMethodName(m, paymentMethods)}</strong></td>
                    <td style={s.td}>{getMoveReferenceDescription(m)}</td>
                    <td style={{ ...s.tdRight, fontWeight: 'bold', color: '#16a34a' }}>
                      {formattedCurrency(Math.abs(Number(m.value) || 0))}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Saídas Analíticas */}
          <div style={{ fontWeight: 'bold', fontSize: '9pt', marginBottom: 4, marginTop: 12, color: '#dc2626' }}>
            Saídas Detalhadas ({outsMoves.length} itens)
          </div>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '50px' }}>ID</th>
                <th style={{ ...s.th, width: '120px' }}>Data / Hora</th>
                <th style={{ ...s.th, width: '130px' }}>Método / Categoria</th>
                <th style={s.th}>Origem / Motivo</th>
                <th style={{ ...s.th, textAlign: 'right', width: '90px' }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {outsMoves.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ ...s.td, textAlign: 'center', color: '#64748b' }}>
                    Nenhuma saída registrada
                  </td>
                </tr>
              ) : (
                outsMoves.map((m, idx) => (
                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={s.td}>#{m.move}</td>
                    <td style={s.td}>{formatClosureDateTime(m.moment)}</td>
                    <td style={s.td}><strong>{getPaymentMethodName(m, paymentMethods)}</strong></td>
                    <td style={s.td}>{getMoveReferenceDescription(m)}</td>
                    <td style={{ ...s.tdRight, fontWeight: 'bold', color: '#dc2626' }}>
                      -{formattedCurrency(Math.abs(Number(m.value) || 0))}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </>
      )}

      {/* Assinaturas */}
      <div style={{ marginTop: '30px', paddingTop: '16px', borderTop: '1px solid #cbd5e1' }}>
        <Grid container spacing={4}>
          <Grid item xs={6} style={{ textAlign: 'center' }}>
            <div style={{ borderTop: '1px solid #334155', width: '80%', margin: '0 auto 6px auto' }} />
            <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>
              {closure?.closedBy || credencials?.name || 'Operador de Caixa'}
            </div>
            <div style={{ fontSize: '8pt', color: '#64748b' }}>Operador Responsável</div>
          </Grid>
          <Grid item xs={6} style={{ textAlign: 'center' }}>
            <div style={{ borderTop: '1px solid #334155', width: '80%', margin: '0 auto 6px auto' }} />
            <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>Conferência / Gerência</div>
            <div style={{ fontSize: '8pt', color: '#64748b' }}>Supervisor / Responsável</div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};
